import { getApiInstance } from '../axiosInterceptor'
import axios from 'axios'

const pendingRequests = {}

// This file contains both cash count functions and transaction-related functions

/**
* Get API endpoint URL
* @returns {string} The base API URL
*/
const getApiEndpoint = () => {
    return process.env.REACT_APP_API_ENDPOINT || 'https://gw.dev.apim.kmart.com.au/kolumbus/api/v2'
};


/**
* Generate a unique request key based on params
* @param {Object} params - Query parameters
* @returns {string} - Unique request key
*/
const getRequestKey = (params = {}) => {
  return `cash_count_${params.month || ''}_${params.year || ''}_${params.status || ''}`
}

/**
* Fetch cash counts based on filter parameters
* @param {Object} msalInstance - MSAL instance for authentication
* @param {Object} params - Filter parameters
* @returns {Promise} Promise that resolves with cash count data
*/
export const fetchCashCounts = async (msalInstance, params = {}) => {
  try {
    // Use provided params or defaults
    const queryParams = {
      month: params.month,
      year: params.year,
      txnType: params.txnType || 'cashCountTxn',
      status: params.status || 'active'
    }

    // Create a unique key for this request
    const requestKey = getRequestKey(queryParams)

    // Check if a request with these exact parameters is already in progress
    if (pendingRequests[requestKey]) {
      return pendingRequests[requestKey]
    }

    // Build query string - properly encode parameters
    const queryString = Object.entries(queryParams)
      .filter(([value]) => value !== undefined && value !== null)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&')

    // Construct API URL with query string
    const apiUrl = `${getApiEndpoint()}/pettyCash/entries?${queryString}`

    // Get API instance and make the request
    const axiosInstance = getApiInstance(msalInstance)

    // Store the promise in our pending requests
    pendingRequests[requestKey] = axiosInstance.get(apiUrl)
      .then(response => {
        // Remove from pending requests after completion
        delete pendingRequests[requestKey]
        return response.data
      })
      .catch(error => {
        // Remove from pending requests on error
        delete pendingRequests[requestKey]
        console.error('Error fetching transactions:', error)
        throw error
      })

    return pendingRequests[requestKey]
  } catch (error) {
    console.error('Error fetching transactions:', error)
    throw error
  }
}

/**
* Create a new cash count record
* @param {Object} cashCountData - The cash count data
* @param {Object} msalInstance - Optional MSAL instance for authentication
* @returns {Promise} Promise that resolves with the creation result
*/

export const createCashCount = async (cashCountData, msalInstance) => {
  try {
    const apiUrl = `${getApiEndpoint()}/pettyCash/createCashCount`

    // Explicitly set headers to ensure proper formatting
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }

    // If MSAL instance is provided, use it to get the API instance
    if (msalInstance) {
      const axiosInstance = getApiInstance(msalInstance)

      const response = await axiosInstance.post(apiUrl, cashCountData)
      return response.data
    } else {
      // Fallback to using token from localStorage
      const token = localStorage.getItem('bearerToken')

      headers['Authorization'] = `Bearer ${token}`

      const response = await axios.post(apiUrl, cashCountData, {
        headers: headers
      })

      return response.data
    }
  } catch (error) {
    console.error('Error creating cash count:', error)
    throw error
  }
}

/**
* Get cash count details by ID
* @param {string} countId - ID of the cash count to retrieve
* @param {Object} msalInstance - MSAL instance for authentication
* @returns {Promise} Promise that resolves with cash count details
*/
export const getCashCountById = async (countId, msalInstance) => {
  try {
    const apiUrl = `${getApiEndpoint()}/pettyCash/cashCount/${countId}`

    const axiosInstance = getApiInstance(msalInstance)
    const response = await axiosInstance.get(apiUrl)
    return response.data
  } catch (error) {
    console.error('Error fetching cash count details:', error)
    throw error
  }
}

/**
* Close the monthly balance - NOTE: This function is for transactions only, not for cash counts
* @param {Object} msalInstance - MSAL instance for authentication
* @param {Object} closeMonthData - Data needed to close the month
* @returns {Promise} Promise that resolves with the API response
*/
export const closeMonthlyBalance = async (msalInstance, closeMonthData) => {
  try {
    const apiUrl = `${getApiEndpoint()}/pettyCash/closeMonthlyBalance`

    // Get API instance and make the request
    const axiosInstance = getApiInstance(msalInstance)
    const response = await axiosInstance.post(apiUrl, closeMonthData)
    return response.data
  } catch (error) {
    console.error('Error closing monthly balance:', error)
    throw error
  }
}